import React, { useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authService } from '../../../services/auth';
import SortableTable from '../../../components/common/SortableTable/SortableTable.jsx';
import Pagination from '../../../components/common/Pagination/Pagination.jsx';
import ConfirmationModal from '../../../components/ui/ConfirmationModal/ConfirmationModal.jsx';
import { useConfirmation } from '../../../hooks/useConfirmation';
import { useDateTime } from '../../../hooks/useDateTime';

// Separate search input component to isolate re-renders
const SearchInput = React.memo(({ value, onChange, placeholder }) => (
    <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="search-input"
    />
));

// Separate action button to prevent re-renders
const ActionButton = React.memo(({ onClick, text, disabled }) => (
    <button 
        onClick={onClick}
        className="btn btn-primary"
        disabled={disabled}
    >
        {text}
    </button>
));

// Loading overlay component
const LoadingOverlay = React.memo(({ loading }) => 
    loading ? (
        <div className="table-loading-overlay">
            <div className="spinner"></div>
            <span>Loading...</span>
        </div>
    ) : null
);

const UserTable = React.memo(({
    hookData,
    onAddUser,
    onEditUser
}) => {
    const { t } = useTranslation(['user', 'common']);
    const currentUser = authService.getCurrentUser();
    const { isOpen, confirmationConfig, confirm, closeConfirmation } = useConfirmation();
    const { formatDateTime } = useDateTime();
    const [deletingUserId, setDeletingUserId] = useState(null);


    const getRoleBadgeClass = useCallback((role) => {
        return role === 'admin' ? 'role-badge admin' : 'role-badge user';
    }, []);

    const translateRole = useCallback((role) => {
        return role === 'admin' ? t('common:roles.admin') : t('common:roles.user');
    }, [t]);

    // Define handleDeleteClick before it's used in columns
    const handleDeleteClick = useCallback(async (user) => {
        const confirmed = await confirm({
            title: t('user:actions.delete_user'),
            message: t('user:actions.delete_confirmation', { username: user.username }),
            confirmText: t('common:buttons.delete'),
            confirmButtonClass: 'btn-danger'
        });

        if (confirmed) {
            setDeletingUserId(user.id);
            await hookData.deleteUser(user.id, user.username);
            setDeletingUserId(null);
        }
    }, [confirm, t, hookData]);

    // Table columns configuration with proper memoization
    const columns = useMemo(() => [
        { 
            key: 'username', 
            label: t('common:labels.username'), 
            sortable: true,
            render: (user) => <strong>{user.username}</strong>
        },
        { 
            key: 'email', 
            label: t('common:labels.email'), 
            sortable: true,
            render: (user) => user.email
        },
        { 
            key: 'role', 
            label: t('common:labels.role'), 
            sortable: true,
            render: (user) => (
                <span className={getRoleBadgeClass(user.role)}>
                    {translateRole(user.role)}
                </span>
            )
        },
        {
            key: 'created_at',
            label: t('user:table.created'),
            sortable: true,
            render: (user) => formatDateTime(user.created_at)
        },
        {
            key: 'actions',
            label: t('common:labels.actions'),
            sortable: false,
            render: (user) => (
                <div className="actions-cell">
                    <button
                        onClick={() => onEditUser(user)}
                        className="action-button edit-button"
                        title={t('user:actions.edit_user')}
                        disabled={hookData.actionLoading}
                    >
                        ✏️
                    </button>
                    {user.id !== currentUser?.id && (
                        <button
                            onClick={() => handleDeleteClick(user)}
                            className="action-button delete-button"
                            title={t('user:actions.delete_user')}
                            disabled={hookData.actionLoading || deletingUserId === user.id}
                        >
                            {deletingUserId === user.id ? '⏳' : '🗑️'}
                        </button>
                    )}
                </div>
            )
        }
    ], [t, formatDateTime, getRoleBadgeClass, translateRole, onEditUser, handleDeleteClick, currentUser?.id, hookData.actionLoading, deletingUserId]);

    // Memoized pagination props
    const paginationProps = useMemo(() => ({
        pagination: hookData.pagination,
        onPageChange: hookData.onPageChange,
        onLimitChange: hookData.onLimitChange,
        limitOptions: hookData.limitOptions
    }), [
        hookData.pagination,
        hookData.onPageChange,
        hookData.onLimitChange,
        hookData.limitOptions
    ]);

    return (
        <div className="user-management-content">
            {/* Search and Action Bar - isolated components */}
            <div className="action-bar">
                <SearchInput
                    value={hookData.search}
                    onChange={hookData.onSearchChange}
                    placeholder={t('user:management.search_placeholder')}
                />
                <ActionButton
                    onClick={onAddUser}
                    text={`${t('common:add')} ${t('user:management.user')}`}
                    disabled={hookData.actionLoading}
                />
            </div>

            {/* Users Table with loading overlay - isolated re-renders */}
            <div style={{ position: 'relative' }}>
                <SortableTable
                    columns={columns}
                    data={hookData.data}
                    sortField={hookData.sort}
                    sortOrder={hookData.order}
                    onSort={hookData.onSortChange}
                    emptyMessage={hookData.search ? t('user:table.no_users_search') : t('user:table.no_users_found')}
                />
                <LoadingOverlay loading={hookData.loading} />
            </div>

            {/* Pagination Controls - memoized */}
            {hookData.pagination && (
                <Pagination {...paginationProps} />
            )}

            <ConfirmationModal
                isOpen={isOpen}
                onClose={closeConfirmation}
                onConfirm={confirmationConfig.onConfirm}
                title={confirmationConfig.title}
                message={confirmationConfig.message}
                confirmText={confirmationConfig.confirmText}
                cancelText={confirmationConfig.cancelText}
                confirmButtonClass={confirmationConfig.confirmButtonClass}
                loading={deletingUserId !== null}
            />
        </div>
    );
});

export default UserTable;